import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Card, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Alert, Stack, ToggleButton, ToggleButtonGroup,
    IconButton, Tooltip as MuiTooltip, Skeleton,
} from '@mui/material';
import { PhoneAndroid, Apple as AppleIcon, Web as WebIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useTheme } from '../../contexts/ThemeContext';
import { formatBRL } from '../../utils/formatBRL';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import { SalesRecord } from '../../types/subscription';
import { getAppleSubscribers } from '../../services/appleSubscribersService';
import { AppleSubscriberRow } from '../../utils/appleSubscribers';

interface ProductSale { sku?: string; title?: string; units: number; gross: number; proceeds: number; }
interface DayPoint { date: string; gross: number; proceeds: number; units: number; }
interface AppleReport {
    grossTotal: number; proceedsTotal: number; unitsTotal: number;
    byProduct: ProductSale[]; byDay: DayPoint[]; daysWithData: string[];
}

const PERIODS = [
    { days: 7, label: '7 dias' },
    { days: 30, label: '30 dias' },
    { days: 90, label: '90 dias' },
    { days: 180, label: '6 meses' },
    { days: 365, label: '1 ano' },
];

const PRODUCT_LABEL: Record<string, string> = {
    'br.com.hostly.basic.monthly': 'Basic Mensal',
    'br.com.hostly.basic.yearly': 'Basic Anual',
    'br.com.hostly.pro.monthly': 'Pro Mensal',
    'br.com.hostly.pro.yearly': 'Pro Anual',
};

const PLAN_LABEL: Record<string, string> = {
    free: 'Free',
    basic: 'Basic',
    premium: 'Basic',
    trial: 'Trial',
    pro: 'Pro',
};

const PERIOD_LABEL: Record<string, string> = {
    monthly: 'Mensal',
    month: 'Mensal',
    annual: 'Anual',
    yearly: 'Anual',
    year: 'Anual',
};

const STATUS_LABEL: Record<string, string> = {
    active: 'Ativo',
    trialing: 'Trial',
    canceled: 'Cancelado',
    expired: 'Expirado',
    inactive: 'Inativo',
};

const planChipLabel = (plan?: string, period?: string) => {
    const p = PLAN_LABEL[String(plan || '').toLowerCase()] || plan || '-';
    const per = period ? (PERIOD_LABEL[String(period).toLowerCase()] || period) : '';
    return per ? `${p} (${per})` : p;
};

const statusLabel = (status?: string) =>
    STATUS_LABEL[String(status || '').toLowerCase()] || status || '-';

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const StatCard: React.FC<{ label: string; value: string; sub?: string; accent: string; spark?: number[]; theme: any; loading?: boolean }> = ({ label, value, sub, accent, spark, theme, loading }) => {
    const sparkData = (spark || []).map((v, i) => ({ i, v }));
    return (
        <Card sx={{
            flex: '1 1 220px', minWidth: 200, p: 2.5,
            backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radii.large, boxShadow: theme.shadows.small,
        }}>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontWeight: 600, mb: 1 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
                <Box>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width={110} height={34} />
                            {sub && <Skeleton variant="text" width={80} height={16} />}
                        </>
                    ) : (
                        <>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>{value}</Typography>
                            {sub && <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>{sub}</Typography>}
                        </>
                    )}
                </Box>
                {sparkData.length > 1 && (
                    <Box sx={{ width: 84, height: 40 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={sparkData} margin={{ top: 4, bottom: 0, left: 0, right: 0 }}>
                                <defs>
                                    <linearGradient id={`sp-${label}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                                        <stop offset="100%" stopColor={accent} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={2} fill={`url(#sp-${label})`} isAnimationActive={!prefersReducedMotion()} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

export const AdminSales: React.FC = () => {
    const { theme } = useTheme();
    const [salesLoading, setSalesLoading] = useState(true);
    const [appleLoading, setAppleLoading] = useState(true);
    const [periodDays, setPeriodDays] = useState(30);
    const [sales, setSales] = useState<SalesRecord[]>([]);
    const [apple, setApple] = useState<AppleReport | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [appleSubs, setAppleSubs] = useState<AppleSubscriberRow[]>([]);
    const [appleSubsLoading, setAppleSubsLoading] = useState(true);
    const [appleError, setAppleError] = useState<string | null>(null);

    const API_URL = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
    const API_KEY = import.meta.env.VITE_API_SECRET_KEY;

    // Firestore (Stripe/web) e relatório Apple carregam SEPARADOS: o painel
    // aparece assim que os dados do Firestore chegam (rápido) e a parte Apple
    // (lenta) entra depois, com skeleton.
    const loadSales = async () => {
        setSalesLoading(true);
        setError(null);
        try {
            const snap = await getDocs(query(collection(db, 'sales'), orderBy('createdAt', 'desc')));
            setSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SalesRecord)).filter(s => s.sessionId !== 'internal_coupon'));
        } catch (err) {
            console.error('Erro ao buscar sales:', err);
            setError('Erro ao carregar dados de vendas.');
        } finally {
            setSalesLoading(false);
        }
    };

    const loadApple = async (days: number) => {
        setAppleLoading(true);
        setAppleError(null);
        try {
            const res = await fetch(`${API_URL}/admin/appstore/sales-report?days=${days}`, {
                headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
            });
            const data = res.ok ? await res.json() : null;
            if (!data) {
                setApple(null);
                setAppleError('Não consegui carregar as vendas da Apple agora.');
            } else if (data.needsVendorNumber) {
                setApple(null);
            } else {
                setApple(data);
                // Sem isso, uma falha total na Apple apareceria como R$ 0,00.
                if (data.allFailed) setAppleError('A App Store não respondeu aos relatórios deste período. Os valores da Apple podem estar incompletos.');
            }
        } catch {
            setApple(null);
            setAppleError('Não consegui carregar as vendas da Apple agora.');
        }
        finally { setAppleLoading(false); }
    };

    const loadAppleSubs = async () => {
        setAppleSubsLoading(true);
        try {
            setAppleSubs(await getAppleSubscribers());
        } catch { setAppleSubs([]); }
        finally { setAppleSubsLoading(false); }
    };

    const reload = (days: number) => { loadSales(); loadApple(days); };

    useEffect(() => { reload(periodDays); }, [periodDays]);
    useEffect(() => { loadAppleSubs(); }, []);

    const loading = salesLoading; // o painel principal depende só do Firestore

    const saleDate = (s: SalesRecord): Date | null => (s.createdAt?.toDate ? s.createdAt.toDate() : null);
    const cutoff = Date.now() - periodDays * 864e5;

    // Stripe/web = Firestore sales nao-apple dentro do periodo (Apple vem do Sales Report)
    const stripeSales = sales.filter(s => s.provider !== 'apple' && (() => { const d = saleDate(s); return d ? d.getTime() >= cutoff : false; })());
    const stripeRevenue = stripeSales.reduce((a, c) => a + (c.value || 0), 0);

    const appleNet = apple?.proceedsTotal || 0;
    const appleGross = apple?.grossTotal || 0;
    const appleUnits = apple?.unitsTotal || 0;
    const totalRevenue = stripeRevenue + appleNet;
    const totalVendas = appleUnits + stripeSales.length;

    // Receita por periodo (Apple liquido + Stripe). Acima de 90 dias a Apple
    // devolve o relatorio mensal, entao o eixo passa a ser por mes.
    const byMonth = (apple as any)?.granularity === 'month';
    // Chave em data local: agrupar por UTC jogava a venda das 21h para o dia seguinte.
    const localKey = (d: Date) => {
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        return byMonth ? iso.slice(0, 7) : iso;
    };
    const dayKey = (t: number) => localKey(new Date(t));
    // Os pontos vem do proprio relatorio; recalcular no cliente desalinhava a janela.
    const dayList: string[] = apple?.byDay?.length
        ? apple.byDay.map((p: any) => p.date)
        : Array.from({ length: periodDays + 1 }, (_, i) => dayKey(Date.now() - (periodDays - i) * 864e5));
    const stripeByDay: Record<string, number> = {};
    stripeSales.forEach(s => { const d = saleDate(s); if (d) stripeByDay[dayKey(d.getTime())] = (stripeByDay[dayKey(d.getTime())] || 0) + (s.value || 0); });
    const appleByDay: Record<string, number> = {};
    (apple?.byDay || []).forEach(p => { appleByDay[p.date] = p.proceeds; });
    const chartData = dayList.map(k => ({ name: byMonth ? k : k.slice(5), value: (appleByDay[k] || 0) + (stripeByDay[k] || 0) }));
    const revSpark = dayList.map(k => (appleByDay[k] || 0) + (stripeByDay[k] || 0));

    // Produtos (Apple report) + stripe agregado
    const stripeByPlatform: Record<string, { units: number; gross: number }> = {};
    stripeSales.forEach(sale => {
        const key = sale.platform === 'android' ? 'Android' : sale.platform === 'ios' ? 'iOS' : 'Web';
        if (!stripeByPlatform[key]) stripeByPlatform[key] = { units: 0, gross: 0 };
        stripeByPlatform[key].units += 1;
        stripeByPlatform[key].gross += sale.value || 0;
    });

    const productRows = [
        ...(apple?.byProduct || []).map(p => ({
            label: PRODUCT_LABEL[p.title || ''] || PRODUCT_LABEL[p.sku || ''] || p.title || p.sku || '-',
            origem: 'Apple',
            plataforma: 'iOS',
            units: p.units,
            gross: p.gross,
            proceeds: p.proceeds,
        })),
        ...Object.entries(stripeByPlatform).map(([plat, v]) => ({
            label: 'Assinatura pelo site',
            origem: 'Stripe',
            plataforma: plat,
            units: v.units,
            gross: v.gross,
            proceeds: v.gross,
        })),
    ];

    // Só entram na lista as compras dentro do período (com data válida).
    const tableSales = sales.filter(s => { const d = saleDate(s); return d ? d.getTime() >= cutoff : false; });

    // Vendas Apple que o relatório contabiliza mas o app não registrou:
    // renovações (acontecem só no lado da Apple) ou compras fora do app.
    // A Apple não informa o comprador nesses casos.
    const identifiedAppleSales = tableSales.filter(s => s.provider === 'apple').length;
    const unidentifiedApple = Math.max(0, appleUnits - identifiedAppleSales);

    const platformIcon = (p?: string) => p === 'ios' ? <AppleIcon fontSize="small" /> : p === 'web' ? <WebIcon fontSize="small" /> : <PhoneAndroid fontSize="small" />;

    // Enquanto o Firestore (rápido) não chega, esqueleto do painel.
    if (loading) {
        return (
            <Box>
                <Skeleton variant="text" width={160} height={34} />
                <Skeleton variant="text" width={260} height={64} />
                <Skeleton variant="text" width={320} height={20} sx={{ mb: 3 }} />
                <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 4 }}>
                    {[0, 1, 2, 3].map(i => (
                        <Skeleton key={i} variant="rounded" height={110} sx={{ flex: '1 1 220px', minWidth: 200, borderRadius: theme.radii.large }} />
                    ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
                    <Skeleton variant="rounded" height={360} sx={{ flex: '2 1 520px', borderRadius: theme.radii.large }} />
                    <Skeleton variant="rounded" height={360} sx={{ flex: '1 1 300px', borderRadius: theme.radii.large }} />
                </Box>
            </Box>
        );
    }

    return (
        <Box>
            {/* Hero + filtro */}
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} gap={2} sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.text.primary, letterSpacing: -0.5 }}>
                        Receita total
                    </Typography>
                    {appleLoading ? (
                        <Skeleton variant="text" width={260} height={64} />
                    ) : (
                        <Typography sx={{
                            fontSize: { xs: '2.6rem', md: '3.4rem' }, fontWeight: 800, lineHeight: 1.1,
                            background: `linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                        }}>
                            R$ {formatBRL(totalRevenue)}
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 0.5 }}>
                        {appleLoading
                            ? `Stripe/web R$ ${formatBRL(stripeRevenue)} · Apple carregando…`
                            : `Stripe/web R$ ${formatBRL(stripeRevenue)} + Apple líquido R$ ${formatBRL(appleNet)} · últimos ${periodDays} dias`}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <ToggleButtonGroup
                        exclusive size="small" value={periodDays}
                        onChange={(_, v) => v && setPeriodDays(v)}
                        sx={{
                            '& .MuiToggleButton-root': { textTransform: 'none', color: theme.colors.text.secondary, borderColor: theme.colors.border },
                            '& .Mui-selected': { backgroundColor: `${theme.colors.primary} !important`, color: '#fff !important' },
                        }}
                    >
                        {PERIODS.map(p => <ToggleButton key={p.days} value={p.days}>{p.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <MuiTooltip title="Recarregar vendas">
                        <span>
                            <IconButton onClick={() => reload(periodDays)} disabled={salesLoading || appleLoading} size="small" sx={{ border: `1px solid ${theme.colors.border}` }}>
                                <RefreshIcon fontSize="small" sx={{ animation: (salesLoading || appleLoading) ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                            </IconButton>
                        </span>
                    </MuiTooltip>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {appleError && <Alert severity="warning" sx={{ mb: 3 }}>{appleError}</Alert>}
            {!appleLoading && !apple && !appleError && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Vendas Apple não configuradas (defina o Vendor Number na aba Apple). Mostrando apenas Stripe/web.
                </Alert>
            )}

            {/* Cards */}
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 4 }}>
                <StatCard theme={theme} accent={theme.colors.primary} label="Receita no período" value={`R$ ${formatBRL(totalRevenue)}`} sub={`${periodDays} dias`} spark={revSpark} loading={appleLoading} />
                <StatCard theme={theme} accent="#2e7d32" label="Apple líquido" value={`R$ ${formatBRL(appleNet)}`} sub={`bruto R$ ${formatBRL(appleGross)}`} loading={appleLoading} />
                <StatCard theme={theme} accent={theme.colors.secondary} label="Stripe / Web" value={`R$ ${formatBRL(stripeRevenue)}`} sub={`${stripeSales.length} venda(s)`} />
                <StatCard theme={theme} accent="#8B6F5C" label="Vendas" value={String(totalVendas)} sub={`${appleUnits} Apple + ${stripeSales.length} Stripe`} loading={appleLoading} />
            </Box>

            {/* Gráfico + produtos */}
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 4 }}>
                <Card sx={{ flex: '2 1 520px', minWidth: 0, p: 2.5, backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.large, boxShadow: theme.shadows.small, height: 360 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary, mb: 2 }}>Receita por dia</Typography>
                    <ResponsiveContainer width="100%" height="88%">
                        <LineChart data={chartData} margin={{ left: -10, right: 8 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: theme.colors.text.secondary }} interval="preserveStartEnd" minTickGap={24} />
                            <YAxis tick={{ fontSize: 11, fill: theme.colors.text.secondary }} />
                            <Tooltip formatter={(v: any) => `R$ ${formatBRL(v)}`} />
                            <Line type="monotone" dataKey="value" stroke={theme.colors.primary} strokeWidth={2.5} dot={false} activeDot={{ r: 6 }} isAnimationActive={!prefersReducedMotion()} />
                        </LineChart>
                    </ResponsiveContainer>
                </Card>
                <Card sx={{ flex: '1 1 300px', minWidth: 0, p: 2.5, backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.large, boxShadow: theme.shadows.small, height: 360, overflowY: 'auto' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary, mb: 2 }}>Por produto</Typography>
                    {productRows.length === 0 ? (
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>Sem vendas no período.</Typography>
                    ) : (
                        <Stack spacing={1.5}>
                            {productRows.map((p, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: theme.radii.medium, backgroundColor: theme.colors.card }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>{p.label}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
                                            <Chip
                                                size="small"
                                                label={`${p.origem} · ${p.plataforma}`}
                                                icon={p.origem === 'Apple' ? <AppleIcon sx={{ fontSize: 14 }} /> : p.plataforma === 'Android' ? <PhoneAndroid sx={{ fontSize: 14 }} /> : <WebIcon sx={{ fontSize: 14 }} />}
                                                sx={{ height: 20, fontSize: '0.68rem', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}` }}
                                            />
                                            <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>{p.units} un.</Typography>
                                        </Box>
                                    </Box>
                                    <Typography sx={{ fontWeight: 700, color: theme.colors.text.primary }}>R$ {formatBRL(p.gross)}</Typography>
                                </Box>
                            ))}
                        </Stack>
                    )}
                </Card>
            </Box>

            {/* Histórico (transações registradas no app) */}
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: theme.colors.text.primary }}>
                Quem comprou ({tableSales.length})
            </Typography>
            <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 1.5 }}>
                Compras identificadas: feitas dentro do app (Apple in-app + Stripe).
            </Typography>
            {!appleLoading && unidentifiedApple > 0 && (
                <Alert severity="info" sx={{ mb: 1.5, borderRadius: theme.radii.medium }}>
                    <b>{unidentifiedApple} venda(s) Apple sem comprador identificado</b> neste período.
                    São <b>renovações</b> ou compras feitas fora do app (ex: resgate de cupom pela App Store).
                    A Apple contabiliza a receita mas <b>não informa quem é o cliente</b> — por isso não aparecem na lista.
                </Alert>
            )}
            <TableContainer component={Card} sx={{ backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.large, boxShadow: theme.shadows.small }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell><TableCell>Usuário</TableCell><TableCell>Plano</TableCell><TableCell>Plataforma</TableCell><TableCell>Valor</TableCell><TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableSales.map((sale) => (
                            <TableRow key={sale.id} hover>
                                <TableCell>{sale.createdAt?.toDate ? sale.createdAt.toDate().toLocaleDateString('pt-BR') : '-'}</TableCell>
                                <TableCell>
                                    <Stack>
                                        <Typography variant="body2">{sale.userName || sale.email}</Typography>
                                        <Typography variant="caption" color="textSecondary">{sale.email}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell><Chip label={planChipLabel(sale.plan, sale.period)} size="small" variant="outlined" sx={{ borderColor: theme.colors.primary, color: theme.colors.primary }} /></TableCell>
                                <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{platformIcon(sale.platform)} {sale.platform}</Box></TableCell>
                                <TableCell>R$ {formatBRL(sale.value)}</TableCell>
                                <TableCell><Chip label={statusLabel(sale.status)} size="small" color={sale.status === 'active' ? 'success' : 'default'} /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Assinantes Apple identificados (via webhook -> subscription/current).
                Identifica quem renovou, já que o relatório agregado da Apple não revela o comprador. */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: theme.colors.text.primary }}>
                    Assinantes Apple identificados ({appleSubs.length})
                </Typography>
                <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 1.5 }}>
                    Quem tem assinatura Apple no app, com a data da última e da próxima renovação. É aqui que dá pra ver quem renovou (a Apple não revela isso no relatório de vendas). "Vencido" = a assinatura passou da data de renovação e não renovou (deixou de pagar).
                </Typography>
                {appleSubsLoading ? (
                    <Skeleton variant="rounded" height={120} />
                ) : appleSubs.length === 0 ? (
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary, py: 2 }}>
                        Nenhum assinante Apple identificado ainda.
                    </Typography>
                ) : (
                    <TableContainer component={Card} sx={{ backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radii.large, boxShadow: theme.shadows.small }}>
                        <Table sx={{ '& td': { fontVariantNumeric: 'tabular-nums' } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Usuário</TableCell><TableCell>Plano</TableCell><TableCell>Última renovação</TableCell><TableCell>Próxima renovação</TableCell><TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {appleSubs.map((s) => (
                                    <TableRow key={s.userId} hover>
                                        <TableCell>
                                            <Stack>
                                                <Typography variant="body2">{s.name || s.email}</Typography>
                                                <Typography variant="caption" color="textSecondary">{s.email}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={planChipLabel(s.plan, s.billingInterval)} size="small" variant="outlined" sx={{ borderColor: theme.colors.primary, color: theme.colors.primary }} />
                                        </TableCell>
                                        <TableCell>{s.lastRenewal ? s.lastRenewal.toLocaleDateString('pt-BR') : '-'}</TableCell>
                                        <TableCell>{s.nextRenewal ? s.nextRenewal.toLocaleDateString('pt-BR') : '-'}</TableCell>
                                        <TableCell>
                                            {s.active ? (
                                                <Chip label="Ativo" size="small" color="success" />
                                            ) : ['active', 'trialing'].includes(String(s.status || '').toLowerCase()) ? (
                                                <Chip label="Vencido" size="small" color="warning" variant="outlined" />
                                            ) : (
                                                <Chip label={statusLabel(s.status)} size="small" color="default" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};
